import VueLib from "vue";
import { Vue, Component } from "vue-property-decorator";
import FormPrefillService, {
  FieldProvenance,
  PrefillPayload
} from "@/services/FormPrefillService";

/**
 * Soft-prefill mixin. Components opt-in by:
 *   1. setting `prefillFormKey` (string) — the form_key registered on the
 *      backend resolver (e.g. "asesmen_awal_ralan").
 *   2. calling `loadPrefill({ pasien_rm, register_id, unit_id })` once data
 *      it needs is ready (usually after the patient identity loads).
 *   3. (optional) calling `prefilledSince(path)` in the template to render a
 *      <PrefillBadge> next to the corresponding input.
 *
 * Merge rule: a field is filled ONLY when the component's current value is
 * empty (null / "" / undefined). User edits are NEVER overwritten — this is
 * the "masih bisa diedit" guarantee from the original requirement.
 *
 * The path strings are dot-notation aligned with the resolver's PATH_MAP,
 * e.g. "tanda_vital.sistole" → vm.tandaVital.sistole.
 */
@Component
export default class PrefillMixin extends Vue {
  prefillFormKey: string | null = null;

  prefillPayload: PrefillPayload | null = null;

  /** path → provenance, only for fields actually applied to the form. */
  prefillApplied: Record<string, FieldProvenance> = {};

  /** path → true, for fields the user explicitly cleared (kept reactive). */
  prefillCleared: Record<string, boolean> = {};

  /**
   * Override this getter in the host component to map a prefill path
   * (e.g. "tanda_vital.sistole") to the reactive property on `this`. By
   * default uses dot-walk into `this.$data`.
   */
  prefillResolvePath(path: string): { get: () => any; set: (v: any) => void } {
    const segments = path.split(".");
    const last = segments.pop() as string;
    const parent = segments.reduce<any>((acc, seg) => {
      if (acc == null) return acc;
      return acc[seg];
    }, this);
    return {
      get: () => (parent ? parent[last] : undefined),
      set: (v: any) => {
        if (parent) parent[last] = v;
      }
    };
  }

  /**
   * Returns provenance metadata if the given path was actually filled by
   * the autofill (and not later cleared by the user). Template:
   *   <PrefillBadge :prov="prefilledSince('tanda_vital.sistole')" />
   */
  prefilledSince(path: string): FieldProvenance | null {
    if (this.prefillCleared[path]) return null;
    return this.prefillApplied[path] || null;
  }

  async loadPrefill(params: {
    pasien_rm: string;
    register_id?: number | string;
    unit_id?: string;
    sources?: string[];
  }) {
    if (!this.prefillFormKey || !params.pasien_rm) return;

    const payload = await FormPrefillService.fetchPrefill(
      params.pasien_rm,
      this.prefillFormKey,
      {
        register_id: params.register_id,
        unit_id: params.unit_id,
        sources: params.sources
      }
    );

    if (!payload) return;
    this.prefillPayload = payload;

    const filled: string[] = [];
    Object.keys(payload.fields).forEach(path => {
      const slot = this.prefillResolvePath(path);
      if (this.isEmptyValue(slot.get())) {
        slot.set(payload.fields[path]);
        filled.push(path);
      }
    });
    // auto-fill stays, but no provenance is recorded so every
    // PrefillBadge and banner (all gated on prefillApplied) renders nothing.
    // Indicators removed per request; to bring them back, repopulate
    // prefillApplied from payload.provenance[path] for each filled path.
    this.prefillApplied = {};

    if (filled.length > 0) {
      // Audit: which fields the form actually auto-filled.
      FormPrefillService.logAcceptance({
        form_key: this.prefillFormKey,
        pasien_rm: params.pasien_rm,
        register_id: params.register_id ?? null,
        unit_id: params.unit_id ?? null,
        event: "accepted",
        fields: filled,
        provenance: {},
        sources: payload.sources
      });
    }
  }

  /**
   * Programmatic clear from the badge: removes the auto-filled value and
   * stops showing the badge for this path.
   */
  prefillClear(path: string) {
    const slot = this.prefillResolvePath(path);
    slot.set(null);
    VueLib.set(this.prefillCleared, path, true);
    VueLib.delete(this.prefillApplied, path);
  }

  private isEmptyValue(v: any): boolean {
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    return false;
  }
}
